/**
 * API «Переотправить фото» (задача B2, SPEC_B2_photo_resend.md) — общая
 * модалка `PhotoResendModal.tsx` дёргает предпросмотр и старт задачи отсюда.
 * Формы ответа повторяют дословно `previewPhotoResend`/`runPhotoResendJob`
 * (`services/marketplace/src/services/marketplace-photo-resend.service.ts`) —
 * бэкенд не трогаем, контракт менять нельзя.
 */

import { api } from '../../shared/api/client'
import type { MarketplaceCode } from '../../shared/marketplaces'

/** Ответы gateway приходят либо напрямую, либо в стандартном конверте `data`. */
const unwrap = (response: any): any => response?.data?.data ?? response?.data ?? {}

/** Ровно один вид входа — карточки, артикулы или весь макет (опционально суженный кабинетами). */
export type PhotoResendInput =
  | { cards: Array<{ article: string; marketplace: string; seller_id: number }> }
  | { articles: string[] }
  | { design_code: string; cabinets?: string[] }

export type PhotoResendReadiness = 'ok' | 'no_cabinet' | 'no_visuals' | 'visual_not_published' | 'too_many_photos'

export interface PhotoResendPreviewPhoto {
  url: string
  checksum: string | null
}

export interface PhotoResendPreviewCardItem {
  card_id: number
  article: string
  marketplace: MarketplaceCode
  seller_id: number
  seller_name: string | null
  cabinet: string | null
  external_id: string
  readiness: PhotoResendReadiness
  photos: PhotoResendPreviewPhoto[]
  photos_count: number
}

export interface PhotoResendPreviewSiteItem {
  article: string
  seller_id: number
}

export interface PhotoResendPreviewResult {
  items: PhotoResendPreviewCardItem[]
  site: {
    items: PhotoResendPreviewSiteItem[]
    site_supported: boolean
  }
  send_enabled: boolean
  ym_write_enabled: boolean
}

export type PhotoResendItemStatus = 'sent' | 'dry_run' | 'channel_disabled' | 'failed' | 'stale' | 'skipped'

export interface PhotoResendItemResult {
  card_id: number
  article: string
  marketplace: MarketplaceCode
  seller_name: string | null
  external_id: string
  status: PhotoResendItemStatus
  reason: string | null
  photos_count: number
  change_id: number | null
  source_change_id: number | null
}

export interface PhotoResendMarketplaceBreakdown {
  sent: number
  dry_run: number
  channel_disabled: number
  failed: number
  stale: number
  skipped: number
}

export interface PhotoResendSiteResult {
  status: 'sent' | 'dry_run' | 'skipped' | 'started' | 'failed'
  reason?: string
  articles_count: number
  site_job_id?: string
  site_run_id?: string
}

export interface PhotoResendJobSummary {
  items: PhotoResendItemResult[]
  by_marketplace: Record<string, PhotoResendMarketplaceBreakdown>
  site: PhotoResendSiteResult
}

export interface PhotoResendStartResult {
  jobId: string
}

/** Причины неготовности по-русски — единственное место форматирования (§ спека задачи D). */
export const PHOTO_RESEND_READINESS_LABELS: Record<Exclude<PhotoResendReadiness, 'ok'>, string> = {
  no_cabinet: 'нет кабинета',
  no_visuals: 'нет визуалов',
  visual_not_published: 'визуалы не опубликованы',
  too_many_photos: 'больше 30 фото для ВБ',
}

/** Коды причин итога задачи (`runPhotoResendJob`) — по-русски; незнакомый код/текст ошибки площадки показывается как есть. */
const PHOTO_RESEND_REASON_LABELS: Record<string, string> = {
  ...PHOTO_RESEND_READINESS_LABELS,
  mixed_pending_changes: 'в наборе карточки есть другая неподтверждённая правка',
  nothing_to_send: 'нечего отправлять',
  site_export_requires_admin: 'перевыгрузка сайта доступна администратору',
}

export function photoResendReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '—'
  if (PHOTO_RESEND_REASON_LABELS[reason]) return PHOTO_RESEND_REASON_LABELS[reason]
  if (reason.startsWith('invalid_status:')) return `набор в статусе ${reason.slice('invalid_status:'.length)}`
  if (reason.startsWith('Канал отправки выключен')) return 'отправка на площадки выключена — выполнен пробный прогон'
  return reason
}

export const photoResendApi = {
  async preview(input: PhotoResendInput): Promise<PhotoResendPreviewResult> {
    const response = await api.post('/cards/photos/resend/preview', input)
    return unwrap(response) as PhotoResendPreviewResult
  },

  /**
   * `publish_confirmed: true` — обязателен всегда (тот же приём, что и у
   * канала изменений): экшен бэкенда без него отклоняет запрос.
   */
  async start(input: PhotoResendInput, options?: { dryRun?: boolean }): Promise<PhotoResendStartResult> {
    const response = await api.post('/cards/photos/resend', {
      ...input,
      publish_confirmed: true,
      ...(options?.dryRun ? { dry_run: true } : {}),
    })
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },
}
